import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../context/AuthContext';
import { usePermission } from '../hooks/usePermission';
import { useTheme } from '../context/ThemeContext';
import NotificationBell from './NotificationBell';
import Icon from '../ui/Icon';
import { APP_VERSION } from '../version';

/* Plan tiers stay inside the brand-blue family (basic → neutral,
   professional → deep blue, enterprise → logo blue). Severity hues
   are reserved for risk meaning and never appear here. */
const PLAN_STYLES = {
  basic: {
    text: 'var(--text-secondary)',
    bg: 'var(--overlay-subtle)',
    border: 'var(--border)',
  },
  professional: {
    text: 'var(--cyan)',
    bg: 'var(--cyan-dim)',
    border: 'color-mix(in srgb, var(--cyan) 30%, transparent)',
  },
  enterprise: {
    text: 'var(--accent)',
    bg: 'var(--accent-dim)',
    border: 'color-mix(in srgb, var(--accent) 35%, transparent)',
  },
};

/* Shared micro-label spec — every uppercase mono tag in the topbar
   uses exactly this scale so badges read as one family. */
const MICRO = {
  fontSize: 9, fontWeight: 'var(--fw-bold)', letterSpacing: '0.08em',
  textTransform: 'uppercase', fontFamily: 'var(--font-mono)',
  borderRadius: 'var(--radius-sm)', padding: '1px 5px', lineHeight: 1.6,
  flexShrink: 0,
};

function orgInitials(name) {
  if (!name) return '?';
  const words = name.trim().split(/\s+/);
  return words.length >= 2
    ? (words[0][0] + words[1][0]).toUpperCase()
    : name.slice(0, 2).toUpperCase();
}

function OrgAvatar({ name, plan, size = 22 }) {
  const c = PLAN_STYLES[plan] || PLAN_STYLES.basic;
  return (
    <span style={{
      width: size, height: size, borderRadius: 'var(--radius)', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: c.bg,
      border: `1px solid ${c.border}`,
      fontSize: Math.round(size * 0.4), fontWeight: 'var(--fw-bold)',
      letterSpacing: '-0.02em',
      color: c.text, userSelect: 'none',
    }}>
      {orgInitials(name)}
    </span>
  );
}

function PlanBadge({ plan }) {
  if (!plan) return null;
  const c = PLAN_STYLES[plan] || PLAN_STYLES.basic;
  return (
    <span style={{ ...MICRO, color: c.text, background: c.bg, border: `1px solid ${c.border}` }}>
      {plan}
    </span>
  );
}

function DemoBadge() {
  return (
    <span style={{
      ...MICRO,
      color: 'var(--warning)',
      background: 'var(--warning-dim)',
      border: '1px solid color-mix(in srgb, var(--warning) 35%, transparent)',
    }}>
      DEMO
    </span>
  );
}

/* ─── Org Switcher ─── */
function OrgSwitcher() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, activeOrg, setActiveOrg, orgs } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const isPlatformAdmin = user?.role === 'admin';

  // Switch org context and, on an actual change, land on the Dashboard so the
  // admin never stays on a page scoped to the previous organization.
  const chooseOrg = (org) => {
    const changed = (org?.id || null) !== (activeOrg?.id || null);
    setActiveOrg(org);
    setOpen(false);
    if (changed) navigate('/dashboard');
  };

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onClick); document.removeEventListener('keydown', onKey); };
  }, [open]);

  // non-admin: tek org → statik badge; birden çok org → admin gibi switcher.
  // (çok-org'lu üyede activeOrg null olabilir = "tüm org'larım" modu)
  const canSwitch = isPlatformAdmin || orgs.length > 1;
  if (!canSwitch) {
    if (!activeOrg) return null;
    const isDemo = activeOrg.customer_type === 'poc';
    return (
      <div className="sh-tb-ctl sh-tb-chip" style={OS.badge} title={t('topbar.orgTitle', { name: activeOrg.name })}>
        {isDemo && <DemoBadge />}
        <OrgAvatar name={activeOrg.name} plan={activeOrg.plan} />
        <div style={OS.badgeText} className="sh-tb-orgtext">
          <span style={OS.orgName}>{activeOrg.name}</span>
          <PlanBadge plan={activeOrg.plan} />
        </div>
      </div>
    );
  }

  // platform admin: switchable dropdown
  const label = activeOrg ? activeOrg.name : t('topbar.allOrganizations');
  const plan  = activeOrg?.plan ?? null;
  const isDemo = activeOrg?.customer_type === 'poc';

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={t('topbar.switchOrgContext')}
        className="sh-tb-ctl"
        style={OS.switchBtn}
      >
        {isDemo && <DemoBadge />}
        {activeOrg
          ? <OrgAvatar name={activeOrg.name} plan={plan} />
          : <span style={OS.platformIcon}><Icon name="domain" size={13} strokeWidth={2} /></span>
        }
        <div style={OS.badgeText} className="sh-tb-orgtext">
          <span style={OS.orgName}>{label}</span>
          {activeOrg && <PlanBadge plan={plan} />}
        </div>
        <Icon
          name="chevronDown"
          size={12}
          strokeWidth={2}
          style={{
            color: 'var(--text-muted)', flexShrink: 0, marginLeft: 2,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform var(--dur-fast) var(--ease-out)',
          }}
        />
      </button>

      {open && (
        <div role="listbox" className="sh-tb-menu" style={OS.dropdown}>
          {/* Dropdown header */}
          <div style={OS.dropdownHeader}>
            <Icon name="organization" size={10} strokeWidth={2.5} style={{ marginRight: 5, opacity: 0.6 }} />
            {t('topbar.orgContext')}
          </div>

          {/* Platform-wide view */}
          <button
            type="button"
            role="option"
            aria-selected={!activeOrg}
            onClick={() => chooseOrg(null)}
            className="sh-tb-item"
            style={OS.item}
          >
            <span style={OS.platformIcon}>
              <Icon name="domain" size={13} strokeWidth={2} />
            </span>
            <div style={OS.itemText}>
              <span style={{ ...OS.itemName, color: !activeOrg ? 'var(--accent)' : 'var(--text-primary)' }}>
                {t('topbar.allOrganizations')}
              </span>
              <span style={OS.itemSub}>{isPlatformAdmin ? t('topbar.platformWideView') : t('topbar.allMyOrganizations')}</span>
            </div>
            {!activeOrg && <Icon name="check" size={12} strokeWidth={2.5} style={{ marginLeft: 'auto', color: 'var(--accent)', flexShrink: 0 }} />}
          </button>

          {orgs.length > 0 && (
            <>
              <div style={OS.divider} />
              <div style={OS.sectionLabel}>{t('topbar.organizations')}</div>
            </>
          )}

          {orgs.map(org => {
            const isActive = activeOrg?.id === org.id;
            const inactive = !org.is_active;
            const orgIsDemo = org.customer_type === 'poc';
            return (
              <button
                key={org.id}
                type="button"
                role="option"
                aria-selected={isActive}
                onClick={() => chooseOrg(org)}
                className="sh-tb-item"
                style={OS.item}
              >
                <OrgAvatar name={org.name} plan={inactive ? null : org.plan} size={26} />
                <div style={{ ...OS.itemText, minWidth: 0 }}>
                  <span style={{
                    ...OS.itemName,
                    color: isActive ? 'var(--accent)' : inactive ? 'var(--text-muted)' : 'var(--text-primary)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {org.name}
                    {inactive && (
                      <span style={{ marginLeft: 5, fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)', fontWeight: 'var(--fw-med)' }}>
                        ({t('topbar.inactive')})
                      </span>
                    )}
                  </span>
                  <span style={OS.itemSub}>
                    {org.user_count != null
                      ? t('topbar.memberCount', { count: org.user_count })
                      : ({ org_admin: t('topbar.roleOrgAdmin'), analyst: t('topbar.roleAnalyst'), viewer: t('topbar.roleViewer') }[org.role] || org.plan || '')}
                  </span>
                </div>
                {orgIsDemo ? <DemoBadge /> : <PlanBadge plan={org.plan} />}
                {isActive && <Icon name="check" size={12} strokeWidth={2.5} style={{ marginLeft: 6, color: 'var(--accent)', flexShrink: 0 }} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const OS = {
  badge: { gap: 8, padding: '0 10px 0 5px' },
  switchBtn: { gap: 8, padding: '0 8px 0 5px' },
  badgeText: { display: 'flex', alignItems: 'center', gap: 6 },
  orgName: {
    fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semi)',
    color: 'var(--text-primary)',
    maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  platformIcon: {
    width: 22, height: 22, borderRadius: 'var(--radius)', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'var(--overlay)', border: '1px solid var(--border-subtle)',
    color: 'var(--text-muted)',
  },
  dropdown: {
    minWidth: 240, maxHeight: 340, overflowY: 'auto',
    padding: '6px 0',
  },
  dropdownHeader: {
    display: 'flex', alignItems: 'center',
    fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', letterSpacing: '0.08em',
    color: 'var(--text-muted)', textTransform: 'uppercase',
    padding: '4px 12px 8px',
    fontFamily: 'var(--font-mono)',
    borderBottom: '1px solid var(--border-subtle)',
    marginBottom: 4,
  },
  sectionLabel: {
    fontSize: 9, fontWeight: 'var(--fw-bold)', letterSpacing: '0.1em',
    color: 'var(--text-muted)', textTransform: 'uppercase',
    padding: '4px 12px 2px',
    fontFamily: 'var(--font-mono)',
  },
  item: { gap: 10, padding: '7px 12px' },
  itemText: { display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 },
  itemName: { fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semi)', lineHeight: 1.3 },
  itemSub: { fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)', lineHeight: 1.2 },
  divider: { height: 1, background: 'var(--border-subtle)', margin: '4px 0' },
};

export const LANG_OPTIONS = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'tr', label: 'Türkçe', flag: '🇹🇷' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
  { code: 'et', label: 'Eesti', flag: '🇪🇪' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'az', label: 'Azərbaycanca', flag: '🇦🇿' },
  { code: 'kk', label: 'Қазақша', flag: '🇰🇿' },
  { code: 'ky', label: 'Кыргызча', flag: '🇰🇬' },
  { code: 'tk', label: 'Türkmençe', flag: '🇹🇲' },
  { code: 'uz', label: 'Oʻzbekcha', flag: '🇺🇿' },
  { code: 'fi', label: 'Suomi', flag: '🇫🇮' },
];

function LanguageSwitcher() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const current = LANG_OPTIONS.find(l => l.code === i18n.language) || LANG_OPTIONS[0];

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onClick); document.removeEventListener('keydown', onKey); };
  }, [open]);

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={t('topbar.changeLanguage')}
        className="sh-tb-ctl"
        style={{ gap: 5, padding: '0 9px', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semi)' }}
      >
        <span style={{ fontSize: 13, lineHeight: 1 }}>{current.flag}</span>
        <span>{current.code.toUpperCase()}</span>
      </button>
      {open && (
        <div role="listbox" className="sh-tb-menu" style={{ minWidth: 170, maxHeight: 320, overflowY: 'auto', padding: '4px 0' }}>
          {LANG_OPTIONS.map(l => {
            const selected = i18n.language === l.code;
            return (
              <button
                key={l.code}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => { i18n.changeLanguage(l.code); setOpen(false); }}
                className="sh-tb-item"
                style={{
                  gap: 8, padding: '8px 12px',
                  fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-med)',
                  color: selected ? 'var(--accent)' : 'var(--text-secondary)',
                  whiteSpace: 'nowrap',
                }}
              >
                <span>{l.flag}</span>
                <span style={{ flex: 1 }}>{l.label}</span>
                {selected && <Icon name="check" size={12} strokeWidth={2.5} style={{ color: 'var(--accent)', flexShrink: 0 }} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const NAV_DEFS = [
  { to: '/dashboard',  key: 'nav.dashboard',   permission: null },
  { to: '/assets',     key: 'nav.assets',      permission: 'asset:list' },
  { to: '/scans',      key: 'nav.scans',       permission: 'scan:list' },
  { to: '/cyber-bulletin', key: 'nav.cyberBulletin', permission: null },
];

const ROLE_CONF = {
  admin:     { color: 'var(--accent)', dot: 'var(--accent)' },
  org_admin: { color: 'var(--warning)', dot: 'var(--warning)' },
  analyst:   { color: 'var(--cyan)', dot: 'var(--cyan)' },
  viewer:    { color: 'var(--text-muted)', dot: 'var(--text-muted)' },
};

/* ─── Theme quick-toggle ─── */
function ThemeToggle() {
  const { t } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';
  return (
    <button
      type="button"
      onClick={toggleTheme}
      aria-label={isDark ? t('nav.lightTheme') : t('nav.darkTheme')}
      title={isDark ? t('nav.lightTheme') : t('nav.darkTheme')}
      className="sh-tb-ctl sh-tb-iconbtn sh-tb-theme"
    >
      <Icon name={isDark ? 'themeLight' : 'themeDark'} size="sm" strokeWidth={1.8} />
    </button>
  );
}

/* ─── Profile menu ─── */
function ProfileMenu({ user }) {
  const { t } = useTranslation();
  const { logout } = useContext(AuthContext);
  const { can } = usePermission();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const rc = ROLE_CONF[user?.role] || ROLE_CONF.viewer;

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const go = (path) => () => { setOpen(false); navigate(path); };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        className="sh-tb-ctl"
        style={S.userChip}
      >
        <div style={{ ...S.avatar, borderColor: rc.dot }}>
          <span style={{ ...S.avatarText, color: rc.color }}>
            {(user?.full_name || user?.email || '?')[0].toUpperCase()}
          </span>
        </div>
        <Icon name="chevronDown" size="xs" strokeWidth={2}
          style={{
            color: 'var(--text-muted)',
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform var(--dur-fast) var(--ease-out)',
          }}
        />
      </button>

      {open && (
        <div role="menu" className="sh-tb-menu" style={S.profileDropdown}>
          <div style={S.profileHeader}>
            <div style={{ ...S.avatar, width: 36, height: 36, borderColor: rc.dot, flexShrink: 0 }}>
              <span style={{ ...S.avatarText, color: rc.color, fontSize: 15 }}>
                {(user?.full_name || user?.email || '?')[0].toUpperCase()}
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, gap: 2 }}>
              <span style={{
                fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semi)',
                color: 'var(--text-primary)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {user?.full_name || user?.email?.split('@')[0]}
              </span>
              <span style={{
                fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)',
                fontFamily: 'var(--font-mono)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {user?.email}
              </span>
              <span style={{
                fontSize: 9, fontWeight: 'var(--fw-bold)', letterSpacing: '0.12em',
                fontFamily: 'var(--font-mono)', color: rc.color,
                marginTop: 1,
              }}>
                {t(user?.role === 'org_admin' ? 'roles.orgAdmin' : `roles.${user?.role || 'viewer'}`).toUpperCase()}
              </span>
            </div>
          </div>

          <div style={S.profileDivider} />

          <button type="button" role="menuitem" className="dr-profile-item"
            style={S.profileItem} onClick={go('/settings')}>
            <Icon name="settings" size="sm" strokeWidth={1.8}/>
            <span>{t('nav.settings')}</span>
          </button>

          <button
            type="button"
            role="menuitem"
            className="dr-profile-item"
            style={S.profileItem}
            onClick={go('/my-organization')}
          >
            <Icon name="organization" size="sm" strokeWidth={1.8}/>
            <span>{t('nav.myOrg')}</span>
          </button>

          {can('user:list') && (
            <button type="button" role="menuitem" className="dr-profile-item"
              style={S.profileItem} onClick={go('/users')}>
              <Icon name="users" size="sm" strokeWidth={1.8}/>
              <span>{t('nav.users')}</span>
            </button>
          )}

          {can('settings:edit') && (
            <button type="button" role="menuitem" className="dr-profile-item"
              style={S.profileItem} onClick={go('/admin-panel')}>
              <Icon name="dashboard" size="sm" strokeWidth={1.8}/>
              <span>{t('nav.adminPanel')}</span>
            </button>
          )}

          <div style={S.profileDivider} />

          <button
            type="button" role="menuitem" className="dr-profile-item dr-profile-item-danger"
            style={{ ...S.profileItem, color: 'var(--danger)' }}
            onClick={() => { setOpen(false); logout(); navigate('/login'); }}
          >
            <Icon name="logout" size="sm" strokeWidth={1.8}/>
            <span>{t('nav.logout')}</span>
          </button>

          <div style={{
            textAlign: 'center',
            fontSize: 'var(--fs-2xs)',
            color: 'var(--text-muted)',
            fontFamily: 'var(--font-mono)',
            padding: '4px 0 2px',
            letterSpacing: '0.04em',
          }}>
            v{APP_VERSION}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Topbar() {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const { can } = usePermission();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <header style={S.bar} className="sh-topbar">
      {/* Logo */}
      <Link to="/dashboard" style={S.logo} className="sh-tb-logo-link" aria-label={t('nav.dashboard')}>
        <img src={isDark ? '/logo-text.png' : '/logo-text-dark.png'} alt="StealerHunt" height={28} className="d-block sh-tb-logo" />
        <span style={S.sep} className="sh-tb-sep" />
      </Link>

      {/* Nav */}
      <nav style={S.nav} className="sh-topbar-nav">
        {NAV_DEFS.filter(n => !n.permission || can(n.permission)).map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) => `sh-tb-nav-link${isActive ? ' is-active' : ''}`}
          >
            {t(item.key)}
          </NavLink>
        ))}
      </nav>

      {/* Right */}
      <div style={S.right} className="sh-topbar-right">
        <OrgSwitcher />

        <LanguageSwitcher />
        <ThemeToggle />
        <NotificationBell />
        <ProfileMenu user={user} />
      </div>
    </header>
  );
}

/* ─── Styles ─── */
const S = {
  bar: {
    height: 64,
    display: 'flex', alignItems: 'center',
    background: 'color-mix(in srgb, var(--bg-topbar) 88%, transparent)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    borderBottom: '1px solid var(--border)',
    padding: '0 20px',
    flexShrink: 0,
    position: 'sticky', top: 0, zIndex: 'var(--z-sticky)',
    gap: 0,
  },
  logo: { display: 'flex', alignItems: 'center', gap: 9, flexShrink: 0, marginRight: 4, cursor: 'pointer', textDecoration: 'none' },
  sep: { width: 1, height: 18, background: 'var(--border)', marginLeft: 18, marginRight: 4 },
  nav: { display: 'flex', alignItems: 'center', gap: 1, flex: 1 },
  right: { display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 },
  creditChip: { gap: 6, padding: '0 10px' },
  creditNum: {
    fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-semi)', color: 'var(--text-primary)',
    fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em',
  },
  creditLbl: {
    fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)',
    textTransform: 'uppercase', letterSpacing: '0.04em',
  },

  userChip: { gap: 5, padding: '0 7px 0 4px' },
  avatar: {
    width: 24, height: 24, borderRadius: '50%',
    border: '1.5px solid', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'var(--overlay-subtle)',
  },
  avatarText: { fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-bold)' },

  /* Profile dropdown */
  profileDropdown: {
    minWidth: 240,
    padding: 6,
    display: 'flex', flexDirection: 'column', gap: 2,
  },
  profileHeader: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 12px',
  },
  profileDivider: { height: 1, background: 'var(--border-subtle)', margin: '4px 6px' },
  profileItem: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '9px 12px', width: '100%', textAlign: 'left',
    background: 'transparent', border: 'none',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--text-secondary)',
    fontSize: 'var(--fs-md)', fontFamily: 'inherit',
    cursor: 'pointer',
    transition: 'background var(--dur-fast) var(--ease-out), color var(--dur-fast) var(--ease-out)',
  },
};
